/**
 * # Scene station + referee home (default-column cell-scene)
 *
 * SCOPE — the **default-column cell-scene** for `DefaultBinder.onBind`'s full-
 * rebuild path ONLY. OUT of scope (named follow-ups): custom binders
 * (`FlightSheetBinder`, the app renderer for every non-default column), legends /
 * overlays / static children (bound through the frozen `sheet.ts`), the
 * `"resize-only"` pass, and `DefaultBinder.rebind`.
 *
 * NO LONGER observe-only. Since the scene-driven commit this is a mutable **station**
 * (engine-spec §3: stations mutate, messages don't) that:
 *
 *   1. `build`s the column's `RenderScene` from the rows/plans/bindings the
 *      binder ALREADY hoisted — it no longer re-resolves bindings (the binder
 *      resolves once per row and passes them in), and it builds regardless of a
 *      ledger (stampless when absent — CHANGED from the old missing-ledger no-op),
 *   2. `diffScene`s it against the retained prior scene, returning the pre-apply
 *      `{ scene, diff }` the binder feeds to `compileSceneCommit`,
 *   3. retains the prior scene per column via `commitScene`, advanced by the
 *      binder ONLY after a successful apply (never past a throw), and exposes the
 *      prior scene's cell row-keys for the compiler's precondition,
 *   4. tallies the commit referee's verdicts (`recordReferee`) + scene failures.
 *
 * The old positional-vs-scene parity CALL (`classifyParity`) is DROPPED here:
 * the new comparator is `commit-referee.ts`, which compares the two compiled
 * bundles' OUTCOMES (not the op-list vs the sceneDiff presence sets). The
 * `scene-parity-classifier.ts` module + its pure suite are untouched.
 *
 * ## τ + epoch
 * `tau` is sampled once per build from the sealed TX0 read mark
 * (`…​.sourceCellData.ledgerBumper?.recorder?.tau() ?? 0`); `epoch` is a monotonic
 * Scene artifact sequence this station increments per build (NOT `boundGeneration`).
 *
 * ## Missing ledger ⇒ stampless build
 * Standalone / legacy sheets have no rows-side ledger; the scene still builds,
 * just without stamps (no version fast-path, no by-reference node reuse — every
 * cell node fresh). This is the L4/L5 degrade-gracefully pattern, now returning a
 * usable scene instead of a total no-op.
 */

import type { Option } from "@tento-lona/sheets";
import { NaiveDate } from "@tento-chrono";
import { type RowLocalId, type Timescale } from "@tento-lona/sheets";
import {
  type CellNode,
  type ColumnKey,
  diffScene,
  type RenderBinding,
  type RenderBindings,
  type RenderPlan,
  type RenderScene,
  Scene,
  type SceneDiff,
  type SceneNodeStamp,
  type SceneRowInput,
} from "@tento-lona/sheets/platform";

import type { SheetRow, SheetViewModel } from "@tento-lona";
import { platformOf } from "@tento-lona/host";

import type { CellBinding } from "./default-binder-helpers.ts";
import type {
  CellRefereeDivergence,
  CellRefereeResult,
  RefereeResult,
} from "./commit-referee.ts";
import type { CellDirective, SceneCommitFailure } from "./commit-bundle.ts";
import {
  AuditRate,
  type RefereeLedger,
  sampleAdmits,
} from "./referee-ledger.ts";
import type { SheetBinder } from "../sheet-index-renderer.ts";

export class LiveSceneRunner {
  /** Prior built scene per column (`column.offset` key) — the memo + diff base. */
  readonly #prevByColumn = new Map<number, RenderScene<ColumnKey>>();

  /**
   * Column offsets whose NEXT eligible build must run regardless of the coin —
   * the paired half of sticky sampling (see `build`). One-shot: consumed by the
   * build it forces.
   */
  readonly #sticky = new Set<number>();

  /** Latest successfully built artifact per mounted column; consumed on commit. */
  readonly #pendingByColumn = new Map<number, RenderScene<ColumnKey>>();

  /** Release scene bases and in-flight artifacts at a sheet/context boundary. */
  reset(): void {
    this.#prevByColumn.clear();
    this.#sticky.clear();
    this.#pendingByColumn.clear();
  }

  /** Retain only mounted columns, including mounted focus/span pins. */
  retainColumns(columns: Iterable<ColumnKey>): void {
    const retained = new Set<number>();
    for (const column of columns) retained.add(column.offset);
    for (const offset of this.#prevByColumn.keys()) {
      if (!retained.has(offset)) this.#prevByColumn.delete(offset);
    }
    for (const offset of this.#sticky) {
      if (!retained.has(offset)) this.#sticky.delete(offset);
    }
    for (const offset of this.#pendingByColumn.keys()) {
      if (!retained.has(offset)) this.#pendingByColumn.delete(offset);
    }
  }

  /** Retire a real DOM column departure, even if its offset is later remounted. */
  retireColumn(column: ColumnKey): void {
    this.#prevByColumn.delete(column.offset);
    this.#sticky.delete(column.offset);
    this.#pendingByColumn.delete(column.offset);
  }

  /**
   * Monotonic scene artifact sequence — the scene `revision.epoch`. Incremented
   * per build. NOT `boundGeneration` (that is the DOM interruptible-render epoch;
   * this counts scene artifacts).
   */
  #epoch = 0;

  // Pull-based aggregate counters (the L2/L5 pattern), read via snapshot().
  #columnsBuilt = 0;
  #nodesBuilt = 0;
  #nodesReused = 0;
  #ledgerReads = 0;
  #deepComparesSkipped = 0;
  #exact = 0;
  #slideClassified = 0;
  #loud = 0;
  #bootstrap = 0;
  #dupFallback = 0;
  #sceneThrew = 0;

  // Per-cell directive classification tallies +
  // cell-referee divergences (with a bounded first-divergence sample).
  #skipClassified = 0;
  #resizeClassified = 0;
  #spanClassified = 0;
  #rebindClassified = 0;
  #cellDivergences = 0;
  #firstCellDivergence: Option<CellRefereeDivergence> = null;

  /**
   * The constructor's `cellRefereeRate`, parsed once (see the ctor doc). Held
   * apart from the raw parameter so the branded value is the ONLY one any code
   * below can reach.
   */
  readonly #cellRefereeAuditRate: AuditRate;

  /**
   * @param ledger durable verdict + sampling counts for this binder instance
   *   (`referee-ledger.ts`). The pull-based tallies below stay as they are; the
   *   ledger is the readable aggregation layer above them, and the only place the
   *   sampling outcome is recorded (the binder cannot see a forced build).
   * @param rate an {@link AuditRate} — fraction of eligible builds that
   *   actually run (`1` = every build, `0` = none). Resolved FRESH on every
   *   build so the caller can force `1` the instant the scene bundle /
   *   directives feed the live path: SAMPLING IS ONLY EVER SOUND WHILE THE
   *   OUTPUT IS PURE AUDIT — a sampled-out bind would starve a live consumer.
   *   Dialing it below 1 trades corpus coverage for cost; a `null` return from
   *   `build` (sampled out) tells the binder to skip the dark work this pass.
   *   Declared `AuditRate | number` because the producer
   *   (`darkSceneSampleRate()`) is a plain `() => number` thunk: THIS class is
   *   the edge that number crosses, so it is where the parse lives.
   *   `sampleAdmits` itself takes the brand and nothing else.
   * @param cellRefereeRate an {@link AuditRate} for the per-cell directive LAYER-2 cell
   *   referee — fraction of classified skip/resize/span cells that capture
   *   fingerprints around the still-executed rebind (`1` = all). Independent of
   *   `rate`; layer 1 (the ledger cross-check) is always-on regardless. Unlike
   *   `rate` it is a CONSTANT, so it is parsed once here rather than per bind —
   *   and a misconfigured one fails at construction, where the stack still
   *   names the binder that supplied it.
   */
  readonly #rate: () => AuditRate | number;
  readonly #ledger: RefereeLedger;
  constructor(
    ledger: RefereeLedger,
    rate: () => AuditRate | number,
    cellRefereeRate: AuditRate | number,
  ) {
    this.#ledger = ledger;
    this.#rate = rate;
    this.#cellRefereeAuditRate = AuditRate.parse(cellRefereeRate);
  }

  /**
   * Build the column's scene from the binder's hoisted rows / plans / bindings
   * and diff it against the retained prior scene — returning the pre-apply
   * `{ scene, diff }`. Does NOT advance the prior scene (see `commitScene`).
   * Returns `null` only when sampled out (`rate < 1`); a ledger-less sheet still
   * builds (stampless). May throw on a genuinely malformed input — the binder
   * wraps the call and degrades (dark: swallow; live: positional fallback).
   *
   * ## Paired sticky sampling
   * Sampling out drops the column's retained prior scene, so the next sampled
   * build BOOTSTRAPS — every cell an insert, every directive a `rebind`, and no
   * cell-referee corpus at all. Left alone, a 5% coin makes two consecutive
   * admissions a 0.25% event and the pure-dark cell referee never fires. So a
   * build that bootstraps marks its column STICKY and its successor runs
   * regardless of the coin: a real diff against a real prior, real directives,
   * a real `refereeCells` pass. Cost is ≈2× the audit rate, and the mark is
   * one-shot — consumed by the build it forces, re-armed only by another
   * bootstrap.
   */
  build(input: LiveSceneRunner.BuildInput): Option<LiveSceneRunner.Built> {
    const rate = this.#admit(input.column);
    if (rate == null) return null;
    const { sheet, column, rows, plans, bindings } = input;
    const platform = platformOf(sheet.handle);

    // The stamp's `(valuesVersion, attrVersion)` pair is sourced from the EXACT
    // same `currentVersions(rowId)` read LR6's render-skip decides on
    // (`RenderSkip.decide` → the same `SourceRowCellData.currentVersions`
    // probe, behind `Sheet.sceneStamp`), so the scene stamp and the LR6 skip
    // can never disagree on whether a row's values or attributes moved — the
    // whole point of the stamp's attribute version.
    const stampProvider = (rowId: RowLocalId): Option<SceneNodeStamp> =>
      platform.sceneStamp(rowId);

    const rowInputs: SceneRowInput<ColumnKey>[] = [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const plan = plans[i];
      if (row == null || plan == null) continue;
      rowInputs.push({
        rowId: row.id,
        rowIndex: plan.rowIndex,
        label: "", // legends are out of scope; the diff ignores them
        bindings: this.#bindingsFor(row, plan, bindings[i] ?? null),
        plansByColumn: new Map<ColumnKey, RenderPlan<ColumnKey>>([[column, plan]]),
        intents: [], // overlays are a named follow-up; not built here
      });
    }

    return this.#buildResolved({
      column,
      rows: rowInputs,
      timescale: sheet.timescale,
      tau: platform.ledgerTau(),
      stampProvider,
    }, rate);
  }

  /** Build pre-resolved rows through the same admission and scene station. */
  buildRows(input: LiveSceneRunner.ResolvedInput): Option<LiveSceneRunner.Built> {
    const rate = this.#admit(input.column);
    return rate == null ? null : this.#buildResolved(input, rate);
  }

  /** Sample before resolving rows or allocating scene inputs. */
  #admit(column: ColumnKey): Option<AuditRate> {
    const offset = column.offset;
    this.#pendingByColumn.delete(offset);
    // Consume the mark before the coin: a forced build is a one-shot debt, paid
    // whether or not the coin would have admitted this build anyway.
    const forced = this.#sticky.delete(offset);
    // The thunk is a plain `() => number` (`darkSceneSampleRate`), so this is
    // the edge: parse before the admission, not inside it. A misconfigured rate
    // therefore throws here, on every build, forced or not.
    const rate = AuditRate.parse(this.#rate());
    if (!sampleAdmits(rate, forced, Math.random)) {
      // Sampled out: this bind still repaints EVERY cell of the column without
      // advancing the retained scene, so that scene is no longer a sound diff
      // base — exactly the drift `invalidate` guards for the rebind / "resize-only"
      // paths, at column scope. Drop it so the next sampled build bootstraps
      // (pool-vs-target membership) instead of diffing against a stale prior and
      // refereeing phantom divergences.
      this.retireColumn(column);
      this.#ledger.recordSampledOut();
      return null;
    }
    this.#ledger.recordSampledIn(forced);

    return rate;
  }

  #buildResolved(input: LiveSceneRunner.ResolvedInput, rate: AuditRate): LiveSceneRunner.Built {
    const offset = input.column.offset;
    const { column, rows, timescale, tau, stampProvider } = input;
    this.#epoch += 1;

    const prior = this.#prevByColumn.get(offset) ?? null;
    if (rate < 1 && prior == null) {
      // Bootstrap ⇒ this build yields no diff-based corpus. Force the successor
      // (paired sampling, see the header). Only meaningful while sampling: at
      // full rate every build already runs. If the successor bootstraps too
      // (an apply threw, so the binder never advanced the scene) the mark
      // re-arms — forcing is exactly right while a column cannot retain a base.
      this.#sticky.add(offset);
    }
    const scene = Scene.build<ColumnKey>({
      columnRange: new NaiveDate.Range(column.pd.start, column.pd.end),
      timescale,
      columns: [column],
      rows,
      revision: { tau, epoch: this.#epoch },
      ...(prior != null ? { prior } : {}),
      ...(stampProvider != null ? { stampProvider } : {}),
    });
    const diff = diffScene(prior, scene);

    this.#columnsBuilt += 1;
    this.#nodesBuilt += scene.work?.nodesBuilt ?? 0;
    this.#nodesReused += scene.work?.nodesReused ?? 0;
    this.#ledgerReads += scene.work?.ledgerReads ?? 0;
    this.#deepComparesSkipped += diff.work.deepComparesSkipped;

    this.#pendingByColumn.set(offset, scene);
    return { scene, diff, prevCellKeys: cellRowKeys(prior) };
  }

  /** The retained prior scene for a column (`null` before its first commit). */
  prevFor(column: ColumnKey): Option<RenderScene<ColumnKey>> {
    return this.#prevByColumn.get(column.offset) ?? null;
  }

  /**
   * Advance the retained prior scene for a column. The binder calls this ONLY
   * after a successful structure+cell apply, so the prior scene never advances
   * past a throw (Codex refinement: a failed bind must be re-diffed next pass).
   * Rejects obsolete, foreign and already committed artifacts without changing the base.
   */
  commitScene(column: ColumnKey, scene: RenderScene<ColumnKey>): boolean {
    if (this.#pendingByColumn.get(column.offset) !== scene) return false;
    this.#pendingByColumn.delete(column.offset);
    this.#prevByColumn.set(column.offset, scene);
    return true;
  }

  /**
   * Prune `rowIds`' cell nodes from the retained prior scene for a column so the
   * NEXT diff treats them as inserts (⇒ rebind, never a wrongful skip). The
   * `DefaultBinder.rebind` single-row path and the `"resize-only"` pass repaint cells
   * WITHOUT advancing the scene, so without this the retained scene would drift
   * and a later diff could skip a cell whose DOM they mutated. Individual-node
   * pruning (a fresh `cells` Map minus the invalidated rows, wrapped in a new
   * `RenderScene`) is used rather than dropping the whole column scene, so
   * untouched survivors still diff clean (and still classify `skip`).
   */
  invalidate(column: ColumnKey, rowIds: Iterable<RowLocalId>): void {
    const prior = this.#prevByColumn.get(column.offset) ?? null;
    const drop = new Set<string>();
    for (const id of rowIds) drop.add(id.toString());
    if (drop.size === 0) return;
    this.#pendingByColumn.delete(column.offset);
    if (prior == null) return;
    const cells = new Map<string, CellNode<ColumnKey>>();
    for (const [serialized, node] of prior.cells) {
      if (drop.has(node.key.row.toString())) continue;
      cells.set(serialized, node);
    }
    this.#prevByColumn.set(column.offset, { ...prior, cells });
  }

  /**
   * Whether to capture LAYER-2 fingerprints for a classified cell this pass
   * (`cellRefereeRate`). Layer 1 (the always-on ledger cross-check) is unaffected.
   *
   * Routed through `sampleAdmits` rather than re-implementing the `>= 1` short
   * circuit: the two rates are the same concept and were the same three lines,
   * so a `NaN` here failed exactly the way it failed there.
   */
  sampleCell(): boolean {
    return sampleAdmits(this.#cellRefereeAuditRate, false, Math.random);
  }

  /** Tally the classified per-cell directive tags for one bind (pull aggregate). */
  recordCellDirectives(directives: readonly CellDirective[]): void {
    for (const directive of directives) {
      switch (directive.tag) {
        case "skip": this.#skipClassified += 1; break;
        case "resize": this.#resizeClassified += 1; break;
        case "span": this.#spanClassified += 1; break;
        case "rebind": this.#rebindClassified += 1; break;
      }
    }
  }

  /** Tally one cell-referee verdict, keeping a bounded first-divergence sample. */
  recordCellReferee(result: CellRefereeResult): void {
    if (result.verdict !== "loud") return;
    this.#cellDivergences += 1;
    if (this.#firstCellDivergence == null) {
      this.#firstCellDivergence = result.firstDivergence ?? null;
    }
  }

  /** Tally one commit-referee verdict (+ whether the scene compile bootstrapped). */
  recordReferee(result: RefereeResult, bootstrapped: boolean): void {
    switch (result.verdict) {
      case "exact": this.#exact += 1; break;
      case "slide-classified": this.#slideClassified += 1; break;
      case "loud": this.#loud += 1; break;
    }
    if (bootstrapped) this.#bootstrap += 1;
  }

  /** Tally a scene-compile failure (duplicate / desync) — the binder falls back. */
  recordSceneFailure(_reason: SceneCommitFailure): void {
    this.#dupFallback += 1;
  }

  /** Tally a thrown scene build/compile — the binder degrades dark / falls back. */
  recordSceneThrew(): void {
    this.#sceneThrew += 1;
  }

  /** Pull-based aggregate snapshot for tests / logs. */
  snapshot(): LiveSceneRunner.Counters {
    return {
      retainedColumns: this.#prevByColumn.size,
      pendingColumns: this.#pendingByColumn.size,
      stickyColumns: this.#sticky.size,
      columnsBuilt: this.#columnsBuilt,
      nodesBuilt: this.#nodesBuilt,
      nodesReused: this.#nodesReused,
      ledgerReads: this.#ledgerReads,
      deepComparesSkipped: this.#deepComparesSkipped,
      exact: this.#exact,
      slideClassified: this.#slideClassified,
      loud: this.#loud,
      bootstrap: this.#bootstrap,
      dupFallback: this.#dupFallback,
      sceneThrew: this.#sceneThrew,
      skipClassified: this.#skipClassified,
      resizeClassified: this.#resizeClassified,
      spanClassified: this.#spanClassified,
      rebindClassified: this.#rebindClassified,
      cellDivergences: this.#cellDivergences,
      firstCellDivergence: this.#firstCellDivergence,
    };
  }

  /**
   * Full `RenderBindings` for a row: spread its hoisted cell binding across
   * row/cell/chrome (the diff only reads `bindings.cell`). A transiently
   * un-cell-bindable row (the binder resolved `null`) emits a placeholder keyed
   * by the plan's descriptor so the scene still carries the presence node.
   */
  #bindingsFor(
    row: SheetRow.Joined.Handle,
    plan: RenderPlan<ColumnKey>,
    binding: Option<CellBinding>,
  ): RenderBindings {
    if (binding != null) return spreadBindings(binding.binding);
    return placeholderBindings(row.id, plan.descriptorType);
  }
}

/** Prior scene's cell row-keys, in scene iteration order (`null` before first
 *  build) — the compiler's precondition anchor. */
function cellRowKeys(
  prior: Option<RenderScene<ColumnKey>>,
): Option<readonly string[]> {
  if (prior == null) return null;
  const keys: string[] = [];
  for (const node of prior.cells.values()) keys.push(node.key.row.toString());
  return keys;
}

/** Spread one resolved cell `RenderBinding` into a full `RenderBindings` (the
 *  `scene-serde` stub pattern). Only `bindings.cell` is read by the diff. */
function spreadBindings(base: RenderBinding): RenderBindings {
  const row: RenderBinding = { ...base, surface: "row" };
  const cell: RenderBinding = { ...base, surface: "cell" };
  const chrome: RenderBinding = { ...base, surface: "chrome" };
  return { row, cell, chrome, capabilities: [chrome, cell, row] };
}

/** Placeholder bindings for a transiently un-cell-bindable row — keyed by the
 *  plan's descriptor so the presence node is still emitted. */
function placeholderBindings(
  rowId: RowLocalId,
  descriptorType: string,
): RenderBindings {
  const base: RenderBinding = {
    surface: "cell",
    requestedRowId: rowId,
    pluginRowId: rowId,
    sourceRowId: rowId,
    pluginId: descriptorType,
    pluginRole: "source",
    rowRole: "source",
    contentType: descriptorType,
    bindingKind: "row-descriptor",
    boundRowId: null,
    boundRendererId: null,
  };
  return spreadBindings(base);
}

export namespace LiveSceneRunner {
  export interface ResolvedInput {
    readonly column: ColumnKey;
    readonly rows: readonly SceneRowInput<ColumnKey>[];
    readonly timescale: Timescale;
    readonly tau: number;
    readonly stampProvider: Option<(rowId: RowLocalId) => Option<SceneNodeStamp>>;
  }

  export interface BuildInput {
    readonly sheet: SheetViewModel.Surface<SheetBinder>;
    /** The default column being bound. */
    readonly column: ColumnKey;
    /** The bind's target rows, in flat render order. */
    readonly rows: readonly SheetRow.Joined.Handle[];
    /** Per-row plans for this column, aligned to `rows` (already computed). */
    readonly plans: readonly RenderPlan<ColumnKey>[];
    /**
     * Per-row hoisted cell bindings, aligned to `rows` (the binder's single
     * `resolveCellBindingOrSkip` per row). `null` for a transiently
     * un-cell-bindable row (height-only bind that pass).
     */
    readonly bindings: readonly Option<CellBinding>[];
  }

  /** The pre-apply artifact `build` returns for the binder to feed the compiler. */
  export interface Built {
    readonly scene: RenderScene<ColumnKey>;
    readonly diff: SceneDiff<ColumnKey>;
    /** Prior scene's cell row-keys (the compiler's precondition anchor). */
    readonly prevCellKeys: Option<readonly string[]>;
  }

  /** The pull aggregate the breadcrumb + tests read. */
  export interface Counters {
    readonly retainedColumns: number;
    readonly pendingColumns: number;
    readonly stickyColumns: number;
    readonly columnsBuilt: number;
    readonly nodesBuilt: number;
    readonly nodesReused: number;
    readonly ledgerReads: number;
    readonly deepComparesSkipped: number;
    readonly exact: number;
    readonly slideClassified: number;
    readonly loud: number;
    readonly bootstrap: number;
    readonly dupFallback: number;
    readonly sceneThrew: number;
    readonly skipClassified: number;
    readonly resizeClassified: number;
    readonly spanClassified: number;
    readonly rebindClassified: number;
    readonly cellDivergences: number;
    readonly firstCellDivergence: Option<CellRefereeDivergence>;
  }
}
